import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';
import { addDocument } from '../lib/addDocument';
import { invalidateAll } from '../lib/dataCache';
import { L } from '../redesign/tokens';

// The header's Upload button on every page: opens the file picker and runs the same single-document pipeline as the
// Cases page (lib/addDocument.js), showing the real outcome in a small banner. Bulk import stays at /import.
const UploadContext = createContext({ openUpload: () => {}, uploading: false });

export function useUpload() {
  return useContext(UploadContext);
}

const TONES = {
  info: { background: '#FFFFFF', border: L.border, color: L.text },
  ok: { background: '#EEF3E6', border: '#CFDDBE', color: L.green },
  err: { background: '#FBEAE4', border: '#EFC7B8', color: '#9E2A1C' },
};

export function UploadProvider({ children }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const openUpload = useCallback(() => {
    if (!busy && fileRef.current) fileRef.current.click();
  }, [busy]);

  const onFilePicked = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    addDocument({
      file, apiFetch, setMessage, setBusy,
      // lists remembered in memory are stale now: drop them so the next visit reads fresh
      afterDone: async () => { invalidateAll(); },
    });
  };

  const value = useMemo(() => ({ openUpload, uploading: busy }), [openUpload, busy]);
  const tone = message ? TONES[message.tone] || TONES.info : null;

  return (
    <UploadContext.Provider value={value}>
      {children}
      <input ref={fileRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={onFilePicked} data-testid="upload-input" />
      {message && (
        <div
          role="status"
          style={{
            position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, maxWidth: 'calc(100vw - 32px)',
            padding: '10px 16px', borderRadius: 12, fontSize: 14, fontFamily: L.sans, boxShadow: '0 8px 24px rgba(46,33,22,0.14)',
            background: tone.background, border: `1px solid ${tone.border}`, color: tone.color,
          }}
        >
          {message.text}
        </div>
      )}
    </UploadContext.Provider>
  );
}
